/**
 * Client for the board API.
 *
 * Two failure modes get first-class types rather than being flattened into "something
 * went wrong", because both have a correct UI response the charter names:
 *
 *   - ConflictError (409): someone else changed the ticket. The current record travels
 *     with the error so the editor can offer a merge instead of losing the edit.
 *   - SessionExpired (401): the 12-hour JWT ran out mid-edit. This must NEVER cost an
 *     unsaved markdown body — the caller re-authenticates and retries the same payload.
 */

const API_BASE = (
  import.meta.env.VITE_API_URL ?? 'https://mle2zqvap3.execute-api.eu-west-2.amazonaws.com/v1'
).replace(/\/$/, '');

const TOKEN_KEY = 'dwlf-tickets.jwt';

export const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null; // Private windows and blocked site data both throw here.
  }
};

export const setToken = (token: string | null): void => {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* A session that cannot be persisted still works for this tab. */
  }
};

export class SessionExpired extends Error {
  constructor() {
    super('Your session has expired. Sign in again — your edits are kept.');
    this.name = 'SessionExpired';
  }
}

export class ConflictError extends Error {
  constructor(
    message: string,
    /** Whichever record the write was for — a ticket or an epic; both are versioned. */
    readonly current: Ticket | Epic,
    readonly currentVersion: number,
  ) {
    super(message);
    this.name = 'ConflictError';
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors: string[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The newest thing that happened to a ticket, and who did it — a comment, a link, an
 * edit, its creation. Computed server-side (`src/repo.ts`) so the board and an agent
 * asking the same question read the same fact rather than each deriving one.
 *
 * Absent on the migrated tickets, whose history stayed in Notion, so it does not replace
 * `createdAt`/`updatedAt` — `recent.ts` is the one place that combination is written down.
 */
export interface LastActivity {
  at: string;
  /** The activity log's own wording — `commented`, `linked blocks DWLF-12`, `updated`. */
  action: string;
  actor: string;
  /** A person, or a named token (I6). Absent on a row that does not say, never guessed. */
  actorKind?: 'user' | 'token';
}

export interface Ticket {
  ref: number;
  name: string;
  status: string;
  epic: string;
  planPriority: string;
  sequencing: string;
  owner: string | null;
  revenueConsequence: string;
  effort: string;
  source: string | null;
  body: string;
  archived: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  /** Absent on a ticket with no activity row at all, and on every response but `GET /v1/tickets`. */
  lastActivity?: LastActivity;
}

export interface ActivityEntry {
  id: string;
  at: string;
  actor: string;
  actorKind: 'user' | 'token';
  action: string;
  changes?: Record<string, { from: unknown; to: unknown }>;
}

export interface Comment {
  id: string;
  at: string;
  author: string;
  authorKind: 'user' | 'token';
  text: string;
}

export type LinkType = 'blocks' | 'blocked-by' | 'relates-to';

export interface Link {
  type: LinkType;
  ref: number;
}

export interface TicketDetail {
  ticket: Ticket;
  activity: ActivityEntry[];
  comments: Comment[];
  links: Link[];
  attachments: unknown[];
}

/**
 * One success criterion on an epic — the definition of done, one line at a time. `metAt`
 * and `metBy` are stamped by the server on the transition into `met` and never restamped.
 */
export interface Criterion {
  id: string;
  text: string;
  met: boolean;
  metAt: string | null;
  metBy: string | null;
}

/** What the editor sends back. Carrying `id` is what preserves an existing tick. */
export interface CriterionInput {
  id?: string;
  text: string;
  met?: boolean;
}

/** An epic. Its NAME is its identity, and is the string every ticket's `epic` holds. */
export interface Epic {
  name: string;
  goal: string;
  criteria: Criterion[];
  archived: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** What the list adds: the counts the board could compute but an agent could not. */
export interface EpicSummary extends Epic {
  ticketCount: number;
  /** Decided against, reported apart from `doneCount`. Fraction: `progressDenominator`. */
  closedCount: number;
  doneCount: number;
  criteriaMet: number;
  criteriaTotal: number;
}

export interface Blocker {
  ref: number;
  status: string | null;
  name: string | null;
}

export interface DependencyEdge {
  from: number;
  to: number;
  kind: 'blocks' | 'relates-to';
}

/** What the link graph means, computed server-side so the MCP gets the same answer. */
export interface DependencyFacts {
  edges: DependencyEdge[];
  readyToStart: number[];
  blockedInProgress: { ref: number; blockedBy: Blocker[] }[];
  chainDepth: Record<number, number>;
  /**
   * Longest run of OUTSTANDING tickets linked by `blocks`, in the order they must be done.
   * Outstanding means neither `Done` nor `Closed` — work decided against sets no remaining
   * length, exactly as delivered work does not.
   */
  criticalPath: number[];
  /**
   * Tickets whose way is clear only because a blocker was CLOSED rather than delivered.
   * Worth re-reading before picking one up: the thing it was designed against is not
   * being built.
   */
  clearedByClosure: { ref: number; closed: Blocker[] }[];
  external: number[];
}

export interface EpicDetail {
  epic: Epic;
  activity: ActivityEntry[];
  tickets: Ticket[];
  dependencies: DependencyFacts;
}

export interface Meta {
  statuses: string[];
  epics: string[];
  planPriorities: string[];
  sequencings: string[];
  efforts: string[];
  linkTypes: string[];
}

interface ErrorBody {
  error?: string;
  details?: { errors?: string[]; currentVersion?: number; current?: Ticket | Epic };
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.ok) return (await response.json()) as T;

  const problem = (await response.json().catch(() => ({}))) as ErrorBody;

  if (response.status === 401) {
    setToken(null);
    // Broadcast rather than redirect. A redirect would unmount whatever is on screen,
    // which is how a 12-hour token expiring mid-edit eats an unsaved markdown body —
    // the one thing the charter says must never happen. The app shows a sign-in prompt
    // over the top and the editor keeps its draft.
    window.dispatchEvent(new CustomEvent('dwlf-session-expired'));
    throw new SessionExpired();
  }
  if (response.status === 409 && problem.details?.current) {
    throw new ConflictError(
      problem.error ?? 'This ticket changed while you were editing it.',
      problem.details.current,
      problem.details.currentVersion ?? problem.details.current.version,
    );
  }
  throw new ApiError(
    problem.error ?? `Request failed (${response.status})`,
    response.status,
    problem.details?.errors ?? [],
  );
}

export const api = {
  authConfig: () => request<{ googleClientId: string }>('GET', '/auth/config'),

  exchangeGoogle: (idToken: string) =>
    request<{ token: string; email: string; expiresIn: number }>('POST', '/auth/google', {
      idToken,
    }),

  whoami: () => request<{ principal: { email: string } }>('GET', '/whoami'),

  meta: () => request<Meta>('GET', '/meta'),

  list: () => request<{ tickets: Ticket[] }>('GET', '/tickets'),

  get: (ref: number) => request<TicketDetail>('GET', `/tickets/${ref}`),

  create: (fields: Partial<Ticket>) =>
    request<{ ticket: Ticket; warnings: string[] }>('POST', '/tickets', fields),

  /** `version` is what makes the 409 possible — omitting it forces the write. */
  update: (ref: number, version: number, fields: Partial<Ticket>) =>
    request<{ ticket: Ticket; warnings: string[] }>('PATCH', `/tickets/${ref}`, {
      version,
      ...fields,
    }),

  archive: (ref: number) => request<{ ref: number; archived: boolean }>('DELETE', `/tickets/${ref}`),

  comment: (ref: number, text: string) =>
    request<{ comment: Comment }>('POST', `/tickets/${ref}/comments`, { text }),

  /** Both directions are written server-side, so only one call is needed. */
  link: (ref: number, type: LinkType, other: number) =>
    request<{ from: number; type: LinkType; to: number }>('POST', `/tickets/${ref}/links`, {
      type,
      ref: other,
    }),

  unlink: (ref: number, type: LinkType, other: number) =>
    request<{ removed: boolean }>('DELETE', `/tickets/${ref}/links/${type}/${other}`),

  listEpics: () => request<{ epics: EpicSummary[] }>('GET', '/epics'),

  /** The epic page in one call: goal, criteria, its tickets, and the dependency facts. */
  getEpic: (name: string) => request<EpicDetail>('GET', `/epics/${encodeURIComponent(name)}`),

  createEpic: (fields: { name: string; goal?: string; criteria?: CriterionInput[] }) =>
    request<{ epic: Epic }>('POST', '/epics', fields),

  /** `version` is what makes the 409 possible — criteria are replaced as a whole list. */
  updateEpic: (
    name: string,
    version: number,
    fields: { goal?: string; criteria?: CriterionInput[] },
  ) =>
    request<{ epic: Epic }>('PATCH', `/epics/${encodeURIComponent(name)}`, { version, ...fields }),

  archiveEpic: (name: string) =>
    request<{ name: string; archived: boolean }>('DELETE', `/epics/${encodeURIComponent(name)}`),

  listTokens: () => request<{ tokens: TokenSummary[] }>('GET', '/tokens'),

  createToken: (label: string, scopes: string[], days: number) =>
    request<{ token: MintedToken }>('POST', '/tokens', { label, scopes, days }),

  revokeToken: (id: string) => request<{ revoked: boolean }>('DELETE', `/tokens/${id}`),
};

export interface TokenSummary {
  id: string;
  label: string;
  scopes: string[];
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
}

export interface MintedToken extends TokenSummary {
  /** The plaintext — shown once, never retrievable again. */
  plaintext: string;
}
